import React, { useState } from "react";
import { Popover, Spin, Descriptions, Tag, Button, Space, Divider } from "antd";
import { LinkOutlined, ContainerOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchBookingById } from "../services/bookingService";
import { Booking } from "../types/booking";
import { getBookingReference } from "../utils/bookingUtils";
import { statusColors, normalizeBookingStatus } from "../utils/bookingStatus";
import { isVesselFinderTrackLink } from "../utils/vesselFinderUtils";
import { VesselFinderMap } from "./VesselFinderMap";

interface BookingReferenceLinkProps {
  text: string;
  className?: string;
}

/**
 * Extracts booking ID from reference format (EFF1234AIR, EFF1234SEA, etc.)
 */
const extractBookingId = (text: string): number | null => {
  // Match patterns like EFF1234AIR, EFF1234SEA, EFF1234, etc.
  const match = text.match(/EFF(\d+)/i);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return null;
};

export const BookingReferenceLink: React.FC<BookingReferenceLinkProps> = ({
  text,
  className,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popoverVisible, setPopoverVisible] = useState(false);

  const bookingId = extractBookingId(text);

  // If text doesn't match booking pattern, just render as-is
  if (!bookingId) {
    return <span className={className}>{text}</span>;
  }

  const handleOpenChange = async (visible: boolean) => {
    setPopoverVisible(visible);

    if (visible && !booking && !loading) {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchBookingById(bookingId);
        if (result.error || !result.data) {
          setError(t("customs.bookingNotFound") || "Booking not found");
        } else {
          setBooking(result.data);
        }
      } catch (err) {
        console.error("Error fetching booking:", err);
        setError(t("customs.errorLoadingBooking") || "Error loading booking");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleViewBooking = () => {
    if (booking) {
      navigate(`/bookings/${booking.id}/view`);
      setPopoverVisible(false);
    }
  };

  // Get container info (handle both array and single object)
  const getContainerInfo = () => {
    if (!booking?.container_info) return null;
    return Array.isArray(booking.container_info)
      ? booking.container_info[0]
      : booking.container_info;
  };

  const popoverContent = (
    <div style={{ maxWidth: 500, minWidth: 350 }}>
      {loading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin />
        </div>
      ) : error ? (
        <div style={{ padding: "10px", color: "#ff4d4f" }}>{error}</div>
      ) : booking ? (
        <div>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={t("bookings.reference")}>
              {getBookingReference(booking)}
            </Descriptions.Item>
            <Descriptions.Item label={t("bookings.status")}>
              <Tag
                color={
                  statusColors[
                    normalizeBookingStatus(
                      booking.status || ""
                    ) as keyof typeof statusColors
                  ] || "default"
                }
              >
                {t(`status.${normalizeBookingStatus(booking.status || "")}`) ||
                  booking.status ||
                  "-"}
              </Tag>
            </Descriptions.Item>
            {booking.transport_type_info && (
              <Descriptions.Item label={t("bookings.transportType")}>
                {Array.isArray(booking.transport_type_info)
                  ? booking.transport_type_info[0]?.name || "-"
                  : booking.transport_type_info.name || "-"}
              </Descriptions.Item>
            )}
            {booking.origin_port_info && (
              <Descriptions.Item label={t("bookings.originPort")}>
                {Array.isArray(booking.origin_port_info)
                  ? booking.origin_port_info[0]?.name || "-"
                  : booking.origin_port_info.name || "-"}
              </Descriptions.Item>
            )}
            {booking.destination_port_info && (
              <Descriptions.Item label={t("bookings.destinationPort")}>
                {Array.isArray(booking.destination_port_info)
                  ? booking.destination_port_info[0]?.name || "-"
                  : booking.destination_port_info.name || "-"}
              </Descriptions.Item>
            )}
            {booking.eta && (
              <Descriptions.Item label={t("bookings.eta")}>
                {new Date(booking.eta).toLocaleDateString()}
              </Descriptions.Item>
            )}
            {booking.company_info && (
              <Descriptions.Item label={t("bookings.company")}>
                {Array.isArray(booking.company_info)
                  ? booking.company_info[0]?.name || "-"
                  : booking.company_info.name || "-"}
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* Container Information */}
          {getContainerInfo() && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <div style={{ marginBottom: 12 }}>
                <Space>
                  <ContainerOutlined />
                  <strong>{t("bookings.container") || "Container"}</strong>
                </Space>
              </div>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label={t("bookings.containerReference")}>
                  {getContainerInfo()?.reference || "-"}
                </Descriptions.Item>
                {getContainerInfo()?.status && (
                  <Descriptions.Item label={t("bookings.status")}>
                    <Tag>{getContainerInfo()?.status}</Tag>
                  </Descriptions.Item>
                )}
                {getContainerInfo()?.eta && (
                  <Descriptions.Item label={t("bookings.eta")}>
                    {new Date(
                      getContainerInfo()?.eta || ""
                    ).toLocaleDateString()}
                  </Descriptions.Item>
                )}
                {(getContainerInfo()?.delayed_etd ||
                  getContainerInfo()?.etd) && (
                  <Descriptions.Item label={t("bookings.etd") || "ETD"}>
                    {new Date(
                      getContainerInfo()?.delayed_etd ||
                        getContainerInfo()?.etd ||
                        ""
                    ).toLocaleDateString()}
                  </Descriptions.Item>
                )}
                {getContainerInfo()?.origin_port_info && (
                  <Descriptions.Item label={t("bookings.originPort")}>
                    {Array.isArray(getContainerInfo()?.origin_port_info)
                      ? getContainerInfo()?.origin_port_info[0]?.name || "-"
                      : getContainerInfo()?.origin_port_info?.name || "-"}
                  </Descriptions.Item>
                )}
                {getContainerInfo()?.destination_port_info && (
                  <Descriptions.Item label={t("bookings.destinationPort")}>
                    {Array.isArray(getContainerInfo()?.destination_port_info)
                      ? getContainerInfo()?.destination_port_info[0]?.name ||
                        "-"
                      : getContainerInfo()?.destination_port_info?.name || "-"}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </>
          )}

          {/* Vessel Finder Map */}
          {getContainerInfo()?.track_and_trace && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <div style={{ marginBottom: 8 }}>
                <strong>
                  {t("bookings.trackingInformation") || "Tracking"}
                </strong>
              </div>
              <div
                style={{
                  width: "100%",
                }}
              >
                {isVesselFinderTrackLink(
                  getContainerInfo()?.track_and_trace || ""
                ) ? (
                  <VesselFinderMap
                    imo={getContainerInfo()?.track_and_trace}
                    height={250}
                    marginBottom={0}
                  />
                ) : (
                  <iframe
                    src={getContainerInfo()?.track_and_trace || ""}
                    style={{
                      width: "100%",
                      height: "250px",
                      border: "none",
                      borderRadius: "4px",
                    }}
                    title="Track and Trace"
                  />
                )}
              </div>
            </>
          )}

          <div style={{ marginTop: 12, textAlign: "right" }}>
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={handleViewBooking}
            >
              {t("customs.viewBooking")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      title={t("customs.bookingDetails") || "Booking Details"}
      trigger={["hover", "click"]}
      open={popoverVisible}
      onOpenChange={handleOpenChange}
      mouseEnterDelay={0.5}
      mouseLeaveDelay={0.1}
    >
      <span
        className={className}
        style={{
          color: "#1890ff",
          cursor: "pointer",
          textDecoration: "underline",
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        {text}
      </span>
    </Popover>
  );
};

/**
 * Component that processes text and wraps booking references with links
 */
export const BookingReferenceText: React.FC<{
  text: string;
  className?: string;
}> = ({ text, className }) => {
  // Pattern to match booking references: EFF followed by digits, optionally followed by any letters
  // This matches EFF1234, EFF1234AIR, EFF1234SEA, etc.
  const bookingPattern = /(EFF\d+[A-Z]*)/gi;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = bookingPattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Add the booking reference as a link
    parts.push(
      <BookingReferenceLink
        key={match.index}
        text={match[0]}
        className={className}
      />
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  // If no matches, return original text
  if (parts.length === 0) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span
      className={className}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
    >
      {parts}
    </span>
  );
};






